import { type FC } from "react";
import { Toolbar, Checkbox, Button, Typography, Divider } from "@mui/material";
import { useSnackbar } from "notistack";
import { ToolbarProgress, useConfirmAction } from "@/shared/components";
import { useTranslation } from "react-i18next";
import { useDeleteTagsMutation, type TagsItem } from "@/partner/entities/tags";

interface TagsListToolbarProps {
  indeterminate: boolean;
  numSelected: number;
  nodes: TagsItem[];
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedItems: { id: string }[];
  onCloseSelected: () => void;
}

export const TagsListToolbar: FC<TagsListToolbarProps> = ({
  indeterminate,
  numSelected,
  nodes,
  onSelectAllClick,
  selectedItems,
  onCloseSelected,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { confirm, dialog: confirmDialog } = useConfirmAction();

  const [deleteTags, { loading: deleteLoading }] = useDeleteTagsMutation({
    onCompleted: () => {
      onCloseSelected();
      enqueueSnackbar(t("tags.toolbar.deleteSuccess"), { variant: "success" });
    },
    onError: (error: { message: string }) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
    refetchQueries: ["TagsList"],
  });

  const onDeleteSelected = () => {
    // До этой правки массовое удаление здесь шло вообще без вопроса.
    confirm({
      title: t("common.actions.delete"),
      text: t("common.toolbar.deleteConfirm"),
      onConfirm: () =>
        deleteTags({
          variables: {
            ids: selectedItems.map((item) => item.id),
          },
        }),
    });
  };

  return (
    <Toolbar sx={{ position: "relative" }}>
      <Checkbox
        color="default"
        indeterminate={indeterminate}
        checked={numSelected === nodes.length}
        onChange={onSelectAllClick}
        sx={{
          marginLeft: "-16px",
          marginRight: {
            xs: 0,
            sm: "16px",
          },
        }}
      />

      <Typography>
        {t("common.toolbar.selectedCount", { count: numSelected })}
      </Typography>

      <Divider
        orientation="vertical"
        variant="middle"
        sx={{
          mx: 1.25,
          height: "2em",
        }}
      />

      <Button
        color="inherit"
        onClick={onDeleteSelected}
        disabled={deleteLoading || numSelected === 0}
      >
        {t("common.actions.delete")}
      </Button>

      <ToolbarProgress loading={deleteLoading} />
      {confirmDialog}
    </Toolbar>
  );
};
